/**
 * The site's theme contract, shared with the product: an explicit `dark` or
 * `light` class on <html>. Unlike the product, the site always sets one — never
 * "absence means dark" — because the class is also what selects the screenshot
 * variant (see Shot.tsx), and an unset class would show neither.
 *
 * The resolution below is duplicated as an inline script in index.html, which has
 * to run before first paint and so cannot import this module. Keep the two in
 * step; e2e/theme.spec.ts covers the inline copy, this file's tests cover here.
 */
export type Theme = "dark" | "light";

export const THEME_STORAGE_KEY = "theme";

/** Matches --ink in tokens.css for each theme; drives <meta name="theme-color">. */
export const THEME_COLOR: Record<Theme, string> = {
  dark: "#221f1c",
  light: "#fdfcfa",
};

/**
 * An explicit stored choice wins; otherwise light.
 *
 * The OS `prefers-color-scheme` is deliberately NOT consulted: light is the site's
 * default, so everyone arriving fresh sees the same page, and dark is something a
 * visitor opts into with the toggle. index.html ships `class="light"` on <html> for
 * the same reason — it is what a no-JS visitor gets.
 */
export function resolveTheme(stored: string | null): Theme {
  return stored === "dark" || stored === "light" ? stored : "light";
}

/** Put the document into a theme. Safe to call before or after hydration. */
export function applyTheme(theme: Theme): void {
  const root = document.documentElement;
  root.classList.remove("dark", "light");
  root.classList.add(theme);
  document.querySelector('meta[name="theme-color"]')?.setAttribute("content", THEME_COLOR[theme]);
}
